import {
  Select as BaseSelect,
  type SelectPositionerProps,
  type SelectRootChangeEventDetails,
} from "@base-ui/react/select";
import classNames from "classnames";
import * as React from "react";

import {
  Check as CheckIcon,
  ChevronDown as ChevronDownIcon,
} from "@/shared/foundation/icons";

import { Field } from "../Field";

import styles from "./Select.module.css";

/** Control density: drives the trigger's height, padding, and font size. */
export type SelectSize = "sm" | "md" | "lg";

const sizeClass: Record<SelectSize, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

/** One option. `value` is what the field reports; `label` is what it shows. */
export interface SelectItem<T> {
  value: T;
  label: string;
  disabled?: boolean;
}

/**
 * Details accompanying a change — most usefully `reason`, which says what
 * caused it.
 */
export type SelectChangeEventDetails = SelectRootChangeEventDetails;

interface SelectPartContextValue {
  size: SelectSize;
  /** Shown by `Select.Value` while nothing is chosen. */
  placeholder: string | undefined;
}

const SelectPartContext = React.createContext<SelectPartContextValue | null>(
  null,
);

function useSelectPartContext(part: string): SelectPartContextValue {
  const context = React.useContext(SelectPartContext);
  if (context === null) {
    throw new Error(
      `<Select.${part}> must be rendered inside a <Select>. The parts read the size and placeholder from it.`,
    );
  }
  return context;
}

export interface SelectRootProps<T> {
  /** The options, in order. */
  items: readonly SelectItem<T>[];

  /** The chosen value, or `null` when nothing is chosen. Controlled only. */
  value: T | null;

  /** Fired with the newly chosen value, and Base UI's event details. */
  onValueChange: (
    value: T | null,
    eventDetails: SelectChangeEventDetails,
  ) => void;

  /**
   * Visible label. Giving one wraps the field in `Field`, which names the
   * trigger and hangs the description and error off it. Use `aria-label` for a
   * field whose name is carried by something else on the page.
   */
  label?: React.ReactNode;

  /** Helper text for the field. */
  description?: React.ReactNode;

  /**
   * Where `description` sits: under the trigger, or between the label and
   * the trigger, for a field whose description says what to pick.
   * @default "below"
   */
  descriptionPlacement?: "above" | "below";

  /** Field error text; also sets `aria-invalid` on the trigger. */
  error?: string;

  /** Shown in the trigger while `value` is `null`. */
  placeholder?: string;

  /**
   * Trigger density. Popup density is unaffected — the rows are the same rows
   * whatever field they hang off.
   * @default "md"
   */
  size?: SelectSize;

  disabled?: boolean;
  required?: boolean;

  /** Identifies the field in a native form; renders the hidden input. */
  name?: string;

  /**
   * Identity test between an item and the chosen value. Needed when values are
   * objects that don't match referentially (e.g. refetched from a server).
   * @default Object.is
   */
  isItemEqualToValue?: (item: T, value: T) => boolean;

  /** Extra class(es) for the field wrapper — size the field here. */
  className?: string;

  /** Accessible name when there is no visible `label`. */
  "aria-label"?: string;

  /** Id of an element that names the field, such as its section's heading. */
  "aria-labelledby"?: string;

  /**
   * Omit to render one `Select.Item` per entry in `items`. Provide to compose
   * groups and separators by hand.
   */
  children?: React.ReactNode;
}

/**
 * A single-select dropdown over a fixed list of options.
 *
 * Reach for `Combobox` instead when the list is long enough to need searching
 * or is fetched from the server.
 *
 * @example
 * ```tsx
 * <Select
 *   items={ENTITY_ITEMS}
 *   value={entity}
 *   onValueChange={setEntity}
 *   label="Entity"
 *   description="What the trigger watches."
 *   size="sm"
 * />
 * ```
 *
 * @example Composed, when the options need groups.
 * ```tsx
 * <Select items={ZONE_ITEMS} value={zone} onValueChange={setZone} label="Time zone">
 *   <Select.Trigger />
 *   <Select.Content>
 *     <Select.Group>
 *       <Select.GroupLabel>Americas</Select.GroupLabel>
 *       {americas.map((item) => (
 *         <Select.Item key={item.label} value={item.value}>{item.label}</Select.Item>
 *       ))}
 *     </Select.Group>
 *   </Select.Content>
 * </Select>
 * ```
 */
function SelectRoot<T>({
  items,
  value,
  onValueChange,
  label,
  description,
  descriptionPlacement = "below",
  error,
  placeholder,
  size = "md",
  disabled = false,
  required = false,
  name,
  isItemEqualToValue,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  children,
}: SelectRootProps<T>) {
  const context = React.useMemo<SelectPartContextValue>(
    () => ({ size, placeholder }),
    [size, placeholder],
  );

  const body = children ?? (
    <>
      <Trigger
        aria-label={label === undefined ? ariaLabel : undefined}
        aria-labelledby={label === undefined ? ariaLabelledBy : undefined}
      />
      <Content>
        {/* Keyed by position: `items` is an ordered list, and neither the
            value (which may be an object) nor the label is guaranteed
            unique. */}
        {items.map((item, index) => (
          <Item key={index} value={item.value} disabled={item.disabled}>
            {item.label}
          </Item>
        ))}
      </Content>
    </>
  );

  // `Field.Root` sits outside `Select.Root`, not inside it: the select reads
  // the field's validation off the context at its own position, so a field
  // nested below it would leave the trigger without `aria-invalid`.
  const descriptionNode = description !== undefined && (
    <Field.Description>{description}</Field.Description>
  );

  return (
    <Field.Root
      className={classNames(styles.field, className)}
      invalid={error !== undefined}
      disabled={disabled}
    >
      <BaseSelect.Root<T>
        items={items}
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        required={required}
        name={name}
        isItemEqualToValue={isItemEqualToValue}
      >
        <SelectPartContext.Provider value={context}>
          {label !== undefined && <Field.Label>{label}</Field.Label>}
          {descriptionPlacement === "above" && descriptionNode}
          {body}
          {descriptionPlacement === "below" && descriptionNode}
          {error !== undefined && <Field.Error match>{error}</Field.Error>}
        </SelectPartContext.Provider>
      </BaseSelect.Root>
    </Field.Root>
  );
}

export interface SelectTriggerProps {
  className?: string;
  /** Accessible name when the field has no visible label. */
  "aria-label"?: string;
  /** Id of an element that names the field when it has no visible label. */
  "aria-labelledby"?: string;
  /** Replaces the default value-plus-chevron row. */
  children?: React.ReactNode;
}

/**
 * The button that opens the popup, showing the chosen label.
 *
 * Forwards its ref to that button, the field's focusable element — so a
 * childless `<Select>` has to be written out as parts to carry one.
 */
const Trigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  function Trigger(
    {
      className,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      children,
    },
    ref,
  ) {
    const { size, placeholder } = useSelectPartContext("Trigger");

    return (
      <BaseSelect.Trigger
        ref={ref}
        className={classNames(styles.trigger, sizeClass[size], className)}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {children ?? (
          <>
            <BaseSelect.Value
              className={styles.value}
              placeholder={placeholder}
            />
            <BaseSelect.Icon className={styles.icon}>
              <ChevronDownIcon aria-hidden />
            </BaseSelect.Icon>
          </>
        )}
      </BaseSelect.Trigger>
    );
  },
);

export interface SelectContentProps {
  children?: React.ReactNode;
  /**
   * Extra class(es) for the popup surface.
   *
   * Set `--select-max-height` in one to cap how tall the popup grows (default
   * `20rem`). It stays within the room between the field and the viewport edge
   * either way.
   */
  className?: string;
  /** @default 4 */
  sideOffset?: number;
  /** @default 8 */
  collisionPadding?: SelectPositionerProps["collisionPadding"];
  side?: SelectPositionerProps["side"];
  align?: SelectPositionerProps["align"];
}

/**
 * The floating popup. Portal, positioning, and z-index are folded in here
 * because they're system decisions, not per-call-site ones.
 */
function Content({
  children,
  className,
  sideOffset = 4,
  collisionPadding = 8,
  side,
  align,
}: SelectContentProps) {
  return (
    <BaseSelect.Portal>
      <BaseSelect.Positioner
        className={styles.positioner}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        side={side}
        align={align}
        alignItemWithTrigger={false}
      >
        <BaseSelect.Popup className={classNames(styles.popup, className)}>
          <BaseSelect.List className={styles.list}>{children}</BaseSelect.List>
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  );
}

export interface SelectItemProps<T> {
  value: T;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

/** One selectable row. */
function Item<T>({ value, disabled, className, children }: SelectItemProps<T>) {
  return (
    <BaseSelect.Item
      value={value}
      disabled={disabled}
      className={classNames(styles.item, className)}
    >
      <BaseSelect.ItemText className={styles.itemText}>
        {children}
      </BaseSelect.ItemText>
      <BaseSelect.ItemIndicator className={styles.itemIndicator}>
        <CheckIcon aria-hidden />
      </BaseSelect.ItemIndicator>
    </BaseSelect.Item>
  );
}

export interface SelectGroupProps {
  children?: React.ReactNode;
  className?: string;
}

/** A named run of rows. Pair with `Select.GroupLabel`. */
function Group({ children, className }: SelectGroupProps) {
  return (
    <BaseSelect.Group className={classNames(styles.group, className)}>
      {children}
    </BaseSelect.Group>
  );
}

export interface SelectGroupLabelProps {
  children?: React.ReactNode;
  className?: string;
}

/** The heading that names a `Select.Group`. */
function GroupLabel({ children, className }: SelectGroupLabelProps) {
  return (
    <BaseSelect.GroupLabel className={classNames(styles.groupLabel, className)}>
      {children}
    </BaseSelect.GroupLabel>
  );
}

export interface SelectSeparatorProps {
  className?: string;
}

/** A rule between groups. */
function Separator({ className }: SelectSeparatorProps) {
  return (
    <BaseSelect.Separator className={classNames(styles.separator, className)} />
  );
}

/**
 * `Object.assign` rather than a plain object literal: it keeps the root's
 * generic call signature, so `<Select items={entities}>` still infers the
 * value type.
 */
export const Select = Object.assign(SelectRoot, {
  Trigger,
  Content,
  Item,
  Group,
  GroupLabel,
  Separator,
});
